import { motion, AnimatePresence } from 'motion/react';
import { AlertTriangle, X, InboxIcon } from 'lucide-react';

// ─── Loading Spinner ──────────────────────────────────────────────────────────
interface SpinnerProps { size?: 'sm' | 'md' | 'lg'; }
export function LoadingSpinner({ size = 'md' }: SpinnerProps) {
  const s = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' };
  return (
    <svg className={`animate-spin ${s[size]} text-current`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  hideCancel?: boolean;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}
export function ConfirmDialog({
  open, title, description,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  hideCancel = false,
  variant = 'danger', onConfirm, onCancel,
}: ConfirmDialogProps) {
  const theme = {
    danger:  { wrap: 'bg-destructive/10 text-destructive', btn: 'bg-destructive text-white hover:bg-red-700' },
    warning: { wrap: 'bg-amber-100 text-amber-600',        btn: 'bg-amber-500 text-white hover:bg-amber-600' },
    info:    { wrap: 'bg-blue-100 text-blue-600',          btn: 'bg-primary text-primary-foreground hover:opacity-90' },
  }[variant];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onCancel}
          />
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 16 }}
            transition={{ type: 'spring', duration: 0.28, bounce: 0.2 }}
            className="relative bg-card rounded-xl p-6 mx-4 max-w-sm w-full shadow-2xl border border-border z-10"
          >
            <button onClick={onCancel} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className={`${theme.wrap} rounded-full p-3 mb-4`}>
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-base font-semibold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground mb-6">{description}</p>
              <div className={`flex w-full ${hideCancel ? '' : 'gap-3'}`}>
                {!hideCancel && (
                  <button
                    onClick={onCancel}
                    className="flex-1 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors active:scale-[0.98]"
                  >
                    {cancelLabel}
                  </button>
                )}
                <button
                  onClick={onConfirm}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors active:scale-[0.98] ${theme.btn}`}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Page Transition wrapper ──────────────────────────────────────────────────
export function PageTransition({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className="flex flex-col h-full"
    >
      {children}
    </motion.div>
  );
}

// ─── Skeleton loaders ─────────────────────────────────────────────────────────
export function SkeletonCard() {
  return (
    <div className="bg-card rounded-lg p-4 border border-border animate-pulse">
      <div className="h-3 bg-muted rounded w-3/4 mb-2.5" />
      <div className="h-6 bg-muted rounded w-1/2 mb-1.5" />
      <div className="h-3 bg-muted rounded w-1/4" />
    </div>
  );
}
export function SkeletonList({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="bg-card rounded-lg p-3 border border-border animate-pulse flex items-center gap-3">
          <div className="h-8 w-8 bg-muted rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-muted rounded w-2/3" />
            <div className="h-2.5 bg-muted rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TransactionSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="bg-card rounded-lg p-3 border border-border animate-pulse">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 space-y-2">
              <div className="h-3.5 bg-muted rounded w-2/3" />
              <div className="h-2.5 bg-muted rounded w-1/2" />
            </div>
            <div className="h-6 w-16 bg-muted rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="h-2.5 bg-muted rounded w-full" />
            <div className="h-2.5 bg-muted rounded w-full" />
            <div className="h-2.5 bg-muted rounded w-4/5" />
            <div className="h-2.5 bg-muted rounded w-3/5" />
          </div>
          <div className="mt-3 h-10 bg-muted/70 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
interface EmptyStateProps { icon?: any; title: string; description?: string; }
export function EmptyState({ icon: Icon = InboxIcon, title, description }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center px-4">
      <div className="bg-muted rounded-full p-4 mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium mb-1">{title}</p>
      {description && <p className="text-xs text-muted-foreground max-w-xs">{description}</p>}
    </div>
  );
}
